import { useEffect, useMemo, useState } from 'react';

import {
  ActionsRequisesDtoActionsRequisesEnum,
  ComputedResponseDtoListActionsRequisesEnum,
  DossierDtoAdminFonctionnel,
  DossierResponseDto,
} from 'lib_api/lib/api/gen';

import { backAlertMessage } from './utils/backAlertMessage';
import { useApi } from './ApiStoreContext';
import { useDossierActionsRequisesContext } from './DossierActionsRequisesContext';

export const actionsRequisesMapping: {
  [key in ActionsRequisesDtoActionsRequisesEnum]: string;
} = {
  [ActionsRequisesDtoActionsRequisesEnum.MAIN_LEVEE]: 'Saisir la main levée',
  [ActionsRequisesDtoActionsRequisesEnum.MODIFIER_COORDONNEES_PROPRIETAIRE]:
    'Coordonnées du propriétaire à compléter',
  [ActionsRequisesDtoActionsRequisesEnum.PROLONGER_MEF]:
    'Prolonger la mise en fourrière administrative',
  [ActionsRequisesDtoActionsRequisesEnum.RENSEIGNER_SORTIE_VEHICULE]:
    'Renseigner la sortie',
  [ActionsRequisesDtoActionsRequisesEnum.SAISIE_NOTIFICATION_MANUELLE]:
    'Notification à éditer',
  [ActionsRequisesDtoActionsRequisesEnum.SAISIE_VHU]:
    "Générer le bon d'enlèvement pour destruction",
  [ActionsRequisesDtoActionsRequisesEnum.TRAITE_EN_FACTURATION]:
    'Marquer comme traité en facturation',
  [ActionsRequisesDtoActionsRequisesEnum.NON_TRAITE_EN_FACTURATION]:
    'Marquer comme non traité en facturation',
  [ActionsRequisesDtoActionsRequisesEnum.ANNULATION_SORTIE_VEHICULE]:
    'Annuler la sortie',
  [ActionsRequisesDtoActionsRequisesEnum.EXPORT_DONNEES_DOSSIER]:
    'Exporter les données du dossier',
  [ActionsRequisesDtoActionsRequisesEnum.SUPPRESSION_DOSSIER]:
    'Supprimer le dossier',
  [ActionsRequisesDtoActionsRequisesEnum.SUPPRESSION_DOSSIER_BY_FO]:
    'Supprimer le dossier',
  [ActionsRequisesDtoActionsRequisesEnum.UPDATE_ENTITES_RATTACHEMENT_ADMIN]:
    'Modifier les entités de rattachement',
  [ActionsRequisesDtoActionsRequisesEnum.UPDATE_ENTITES_RATTACHEMENT_FO_AF]:
    'Modifier les entités de rattachement',
  [ActionsRequisesDtoActionsRequisesEnum.MODIFICATION_SORTIE_VEHICULE]:
    'Modifier les données de sortie du véhicule',
  [ActionsRequisesDtoActionsRequisesEnum.UPDATE_DONNEES_VEHICULE]:
    "Modifier les données du véhicule ou de l'infraction",
  [ActionsRequisesDtoActionsRequisesEnum.UPDATE_DONNEES_VEHICULE_JUSTICE]:
    "Modifier les données du véhicule ou de l'infraction",
  [ActionsRequisesDtoActionsRequisesEnum.UPDATE_CLASSEMENT]:
    'Modifier le classement',
  [ActionsRequisesDtoActionsRequisesEnum.RENSEIGNER_STATUT_RETOUR_NOTIFICATION_MANUELLE]:
    'Renseigner le statut de retour de la notification manuelle',
  [ActionsRequisesDtoActionsRequisesEnum.CORRIGER_DONNEES_NOTIFICATION_MANUELLE]:
    'Modifier les informations de notification manuelle',
  [ActionsRequisesDtoActionsRequisesEnum.ENREGISTRER_ENTREE]:
    'Finaliser l’enregistrement de l’entrée',
  [ActionsRequisesDtoActionsRequisesEnum.CONTROLER_DONNEES_VEHICULE]:
    'Contrôler les données du véhicule',
  [ActionsRequisesDtoActionsRequisesEnum.CONTROLE_DONNEES_ERREUR_IDENTIFICATION_VEHICULE]:
    'Relancer la vente (WW / maquillé / plaque erronée)',
  [ActionsRequisesDtoActionsRequisesEnum.RELANCE_VENTE_VOL]:
    'Relancer la vente (vol)',
  [ActionsRequisesDtoActionsRequisesEnum.RELANCE_VENTE_IMMO_PJ]:
    'Relancer la vente (IMMO_PJ)',
  [ActionsRequisesDtoActionsRequisesEnum.RELANCE_VENTE_OPPOSITION]:
    'Relancer la vente (OPPOSITION)',
  [ActionsRequisesDtoActionsRequisesEnum.RELANCE_VENTE_DOUBLON]:
    'Relancer la vente (doublon)',
  [ActionsRequisesDtoActionsRequisesEnum.RELANCE_VENTE_MANQUANT]:
    'Relancer la vente (véhicule manquant)',
  [ActionsRequisesDtoActionsRequisesEnum.RECUPERATION_COURRIER_AUTOMATIQUE]:
    'Récupérer le courrier de notification',
  [ActionsRequisesDtoActionsRequisesEnum.RECUPERATION_AR_PND_AUTOMATIQUE]:
    "Récupérer l'AR ou le PND de notification",
  [ActionsRequisesDtoActionsRequisesEnum.CONTROLE_BLOCAGE_ARGOS]:
    'Dossiers bloqués dans le workflow Vol',
  [ActionsRequisesDtoActionsRequisesEnum.CONTROLE_BLOCAGE_ANTAI]:
    'Dossiers bloqués dans le workflow Notification',
  [ActionsRequisesDtoActionsRequisesEnum.CONTROLE_BLOCAGE_DNID]:
    'Dossiers bloqués dans le workflow Aliénation',
  [ActionsRequisesDtoActionsRequisesEnum.RENSEIGNER_INFORMATIONS_SIV]:
    'Renseigner les informations du siv',
  [ActionsRequisesDtoActionsRequisesEnum.CONFIRMER_VOL_VVNR]:
    "Confirmer le caractère volé d'un véhicule",
  [ActionsRequisesDtoActionsRequisesEnum.VALIDATION_ACTIONS_VVNR_HORS_SIF]:
    "Valider les actions externes liées au vol d'un véhicule",
  [ActionsRequisesDtoActionsRequisesEnum.IMPORTER_PIECES_JOINTES]:
    'Joindre un document',
  [ActionsRequisesDtoActionsRequisesEnum.CONFIRMER_NON_IDENTIFIABILITE_PROPRIETAIRE]:
    "Renseigner que le propriétaire n'est pas identifiable",
  [ActionsRequisesDtoActionsRequisesEnum.UPDATE_DONNEES_JUDICIAIRE]:
    'Modifier les données judiciaires',
};

export function useActionsRequises():
  | ActionsRequisesDtoActionsRequisesEnum[]
  | undefined {
  const [actionsRequises, setActionsRequises] =
    useState<ActionsRequisesDtoActionsRequisesEnum[]>();
  const { DossierControllerApi } = useApi();

  useEffect(() => {
    DossierControllerApi.getActionsRequisesByProfileUsingGET()
      .then(({ actionsRequises }) => {
        setActionsRequises(actionsRequises);
      })
      .catch(backAlertMessage);
  }, [DossierControllerApi]);

  return actionsRequises;
}

function useDisplayActionsRequisesBannerInternal(
  currentActionsRequises?: ComputedResponseDtoListActionsRequisesEnum[],
): void {
  const { setDossierActionsRequises } = useDossierActionsRequisesContext();
  // display the actions requises banner(s)
  useEffect(() => {
    if (!!currentActionsRequises && currentActionsRequises.length > 0) {
      setDossierActionsRequises(currentActionsRequises);
    }
    return () => setDossierActionsRequises([]);
  }, [setDossierActionsRequises, currentActionsRequises]);
}

export function useDisplayActionsRequisesBanner(
  dossier: DossierResponseDto,
  profileActionsRequises?: ActionsRequisesDtoActionsRequisesEnum[],
): void {
  const currentActionsRequises = useMemo<
    ComputedResponseDtoListActionsRequisesEnum[] | undefined
  >(
    () =>
      dossier.computed?.listActionsRequises?.filter(actionRequise =>
        profileActionsRequises?.includes(
          ActionsRequisesDtoActionsRequisesEnum[actionRequise],
        ),
      ),
    [dossier, profileActionsRequises],
  );
  useDisplayActionsRequisesBannerInternal(currentActionsRequises);
}

export function useDisplayActionsRequisesBannerAdmin(
  dossier: DossierDtoAdminFonctionnel,
  profileActionsRequises?: ActionsRequisesDtoActionsRequisesEnum[],
): void {
  const currentActionsRequises = useMemo<
    ComputedResponseDtoListActionsRequisesEnum[] | undefined
  >(
    () =>
      dossier.actionsRequises
        ?.filter(actionRequise =>
          profileActionsRequises?.includes(
            ActionsRequisesDtoActionsRequisesEnum[actionRequise],
          ),
        )
        .map(
          actionRequise =>
            ComputedResponseDtoListActionsRequisesEnum[actionRequise],
        ),
    [dossier, profileActionsRequises],
  );
  useDisplayActionsRequisesBannerInternal(currentActionsRequises);
}
